
import { Company } from "@/types";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Building } from "lucide-react";
import { UserRole } from "@/types/enums";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface CompanyListProps {
  companies: Company[];
  isLoading: boolean;
  onEdit: (company: Company) => void;
  onDelete: (id: string) => void;
}

export const CompanyList = ({ 
  companies, 
  isLoading,
  onEdit,
  onDelete
}: CompanyListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (companies.length === 0) {
    return (
      <div className="text-center py-8">
        <Building className="h-12 w-12 mx-auto text-muted-foreground" />
        <h3 className="mt-4 text-lg font-medium">No companies yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Add your first company to get started
        </p>
      </div>
    );
  }
  
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Domains</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {companies.map((company) => (
          <TableRow key={company.id}>
            <TableCell className="font-medium">{company.name}</TableCell>
            <TableCell>{company.contactPerson || '-'}</TableCell>
            <TableCell>{company.contactEmail}</TableCell>
            <TableCell>{formatRole(company.contactRole || UserRole.APP_OWNER)}</TableCell>
            <TableCell>
              {company.domains && company.domains.length > 0 
                ? company.domains.join(", ")
                : '-'}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => onEdit(company)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => onDelete(company.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

function formatRole(role: string): string {
  switch (role) {
    case UserRole.APP_OWNER:
      return 'App Owner';
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    case UserRole.HR:
      return 'HR';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
  }
}
